import { useRef } from 'react';

import ReactToPrint from 'react-to-print';
import { FcPrint } from 'react-icons/fc';
import styled from 'styled-components';

import { GetMicrostoreColumnQuery, useGetMicrostoreColumnPricesQuery } from 'data/__generated__';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { formatPrice } from 'utils/price';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { useAuth } from 'contexts/AuthContext';

type MicrostoreColumn = GetMicrostoreColumnQuery['microstoreColumn'];
type MicrostoreLocation = NonNullable<MicrostoreColumn['shelves'][number]['locationTable'][number][number]>;

export const PrintMicrostoreColumn = ({ microstoreColumn }: { microstoreColumn: MicrostoreColumn }) => {
    const componentRef = useRef(null);
    const { user } = useAuth();
    const userName = [user?.profile.firstname, user?.profile.lastname].filter(Boolean).join(' ') || '-';

    const { data: pricesData } = useGetMicrostoreColumnPricesQuery({
        variables: { microstoreColumnId: microstoreColumn._id },
    });

    const priceTTCByProductId = new Map<string, number>();
    for (const productPrice of pricesData?.microstoreColumnPrices ?? []) {
        priceTTCByProductId.set(productPrice.productId, productPrice.priceTTC);
    }

    return (
        <div className="mx-2">
            <ReactToPrint
                trigger={() => (
                    <span>
                        <TotemPrimaryButton type="button" $isSecondaryStyle>
                            <PrintButtonContent>
                                <FcPrint size={18} />
                                <PrintLabel>Imprimer</PrintLabel>
                            </PrintButtonContent>
                        </TotemPrimaryButton>
                    </span>
                )}
                content={() => componentRef.current}
                documentTitle={`${new Date().toISOString().split('T')[0]} - ${microstoreColumn.siteName} - ${microstoreColumn.name}`}
            />
            <HiddenComponent>
                <Printable ref={componentRef}>
                    <ColumnTitle>{microstoreColumn.name}</ColumnTitle>

                    <InfoSection>
                        <InfoColumn>
                            <InfoRow>
                                <InfoKey>Site</InfoKey>
                                <InfoVal>{microstoreColumn.siteName}</InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Organisation</InfoKey>
                                <InfoVal>{microstoreColumn.site?.organizationName ?? '-'}</InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Groupe</InfoKey>
                                <InfoVal>{microstoreColumn.columnGroupName}</InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Template</InfoKey>
                                <InfoVal>{microstoreColumn.microstoreColumnTemplateName}</InfoVal>
                            </InfoRow>
                        </InfoColumn>
                        <InfoColumn>
                            <InfoRow>
                                <InfoKey>Integration</InfoKey>
                                <InfoVal>
                                    {microstoreColumn.externalColumnType}
                                    {microstoreColumn.externalColumnId ? ` (${microstoreColumn.externalColumnId})` : ''}
                                </InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Date de debut</InfoKey>
                                <InfoVal>{microstoreColumn.startingDate ?? '-'}</InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Prix gérés par la TotAdmin</InfoKey>
                                <InfoVal>{microstoreColumn.shouldSendNayaxPrices ? 'Oui' : 'Non'}</InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Gestion des stocks par la TotAdmin</InfoKey>
                                <InfoVal>{microstoreColumn.shouldUpdateStockOnTransaction ? 'Oui' : 'Non'}</InfoVal>
                            </InfoRow>
                        </InfoColumn>
                        <InfoColumn>
                            {/* <InfoRow>
                                <InfoKey>ID</InfoKey>
                                <InfoVal>{microstoreColumn._id}</InfoVal>
                            </InfoRow> */}
                            <InfoRow>
                                <InfoKey>Creation</InfoKey>
                                <InfoVal>
                                    {getExtendedFormattedDatetime(new Date(microstoreColumn.createdAt).getTime())}
                                </InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Derniere MAJ</InfoKey>
                                <InfoVal>
                                    {getExtendedFormattedDatetime(new Date(microstoreColumn.updatedAt).getTime())}
                                </InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Impression</InfoKey>
                                <InfoVal>{new Date().toLocaleString()}</InfoVal>
                            </InfoRow>
                            <InfoRow>
                                <InfoKey>Imprimé par</InfoKey>
                                <InfoVal>{userName}</InfoVal>
                            </InfoRow>
                        </InfoColumn>
                    </InfoSection>

                    <MDBTable>
                        {microstoreColumn.shelves.map((shelf) =>
                            shelf.locationTable.map((locationRow, locationRowIndex) => (
                                <MDBRow key={`${shelf._id}-row-${locationRowIndex}`}>
                                    {locationRow.map((location: MicrostoreLocation, locationColumnIndex) => {
                                        const productName =
                                            location.microstoreLocationTemplate?.productArrangement?.name ||
                                            location.microstoreLocationTemplate?.product?.fullname ||
                                            '-';
                                        const isDoubleCoil =
                                            location.microstoreLocationTemplate?.product?.isDoubleCoil || false;
                                        const stockMax = location.microstoreLocationTemplate?.stockMax;
                                        const productId = location.microstoreLocationTemplate?.product?._id;
                                        const priceTTC = productId ? priceTTCByProductId.get(productId) : undefined;

                                        return (
                                            <MDBCell
                                                key={`${shelf._id}-${locationColumnIndex}`}
                                                $isDoubleCoil={isDoubleCoil}
                                            >
                                                <MDBCode>{location.nayaxMDBCode ?? '-'}</MDBCode>
                                                <MDBProductName>
                                                    {isDoubleCoil ? `[2x] ${productName}` : productName}
                                                </MDBProductName>
                                                <MDBDetails>
                                                    Capacité: {stockMax ?? '-'} | Prix TTC:{' '}
                                                    {priceTTC !== undefined ? formatPrice(priceTTC) : '-'}
                                                </MDBDetails>
                                            </MDBCell>
                                        );
                                    })}
                                </MDBRow>
                            )),
                        )}
                    </MDBTable>
                </Printable>
            </HiddenComponent>
        </div>
    );
};

const PrintButtonContent = styled.div`
    display: flex;
    align-items: center;
`;

const PrintLabel = styled.span`
    margin-left: 5px;
`;

const HiddenComponent = styled.div`
    display: none;
`;

const Printable = styled.div`
    color: black;
    font-family: Arial, sans-serif;

    @page {
        size: landscape;
        margin: 8mm 10mm;
    }
`;

const ColumnTitle = styled.h1`
    text-align: center;
    font-size: 18px;
    margin: 0 0 8px;
`;

const InfoSection = styled.div`
    display: flex;
    gap: 24px;
    font-size: 10px;
    border-bottom: 1px solid #999;
    padding-bottom: 8px;
    margin-bottom: 10px;
`;

const InfoColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
`;

const InfoRow = styled.div`
    display: flex;
    gap: 4px;
`;

const InfoKey = styled.span`
    font-weight: 700;
    white-space: nowrap;

    &::after {
        content: ':';
    }
`;

const InfoVal = styled.span`
    color: #333;
`;

const MDBTable = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const MDBRow = styled.div`
    display: flex;
    width: 100%;
`;

const MDBCell = styled.div<{ $isDoubleCoil?: boolean }>`
    flex: ${({ $isDoubleCoil }) => ($isDoubleCoil ? '2 1 0' : '1 1 0')};
    border: 1px solid #333;
    padding: 4px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
`;

const MDBCode = styled.div`
    font-size: 22px;
    font-weight: 800;
    line-height: 1.1;
`;

const MDBProductName = styled.div`
    font-size: 8px;
    line-height: 1.2;
    word-break: break-word;
`;

const MDBDetails = styled.div`
    font-size: 7px;
    color: #555;
`;
